import { Tooltip } from '@odo/shared-ui';
import type { RouteInfo } from '../../../services/iam';
import type { PermissionLookup } from '../iamLookups';
import { MethodTag } from '../primitives/MethodTag';
import { PermName } from '../primitives/PermName';

interface RouteRowProps {
    route: RouteInfo;
    permissionLookup: PermissionLookup;
}

// Path cell. Tooltip is unconditional (not scrollWidth-gated) since the fixed
// w-[280px] span never resized after the mono font finished loading, silently
// losing tooltips on long paths - a redundant tooltip is the cheaper failure.
function PathCell({ path }: { path: string }) {
    return (
        <Tooltip content={path} side="top">
            <span className="font-mono text-[12px] text-gray-700 truncate flex-1 min-w-0">
                {path}
            </span>
        </Tooltip>
    );
}

// One row states its guard: migrated names its permission, legacy names the
// guard(s), public says why, open says so plainly (the only real hole).
function GuardCell({ route, permissionLookup }: RouteRowProps) {
    if (route.status === 'migrated' && route.permission) {
        const entry = permissionLookup.get(route.permission);
        return (
            <PermName
                permission={route.permission}
                group={entry?.group ?? 'unknown'}
                // Defaults to unwired; a lookup miss means a registry/route mismatch worth surfacing.
                wired={entry?.wired ?? false}
            />
        );
    }
    if (route.status === 'public') {
        return (
            <span className="text-[12px] text-sky-700">
                No permission required - {route.publicReason}
            </span>
        );
    }
    if (route.status === 'legacy' && route.legacyGuards.length > 0) {
        return (
            <div className="flex items-center gap-1.5 flex-wrap">
                {route.legacyGuards.map((guard) => (
                    <span
                        key={guard}
                        className="font-mono text-[11px] text-gray-600 bg-gray-100 rounded px-1.5 py-0.5"
                    >
                        {guard}
                    </span>
                ))}
            </div>
        );
    }
    return <span className="text-[12px] font-semibold text-rose-600">No guard - open</span>;
}

// Method, path, and guard (right-aligned). No separate status badge -
// GuardCell's own rendering already distinguishes all four states.
export function RouteRow({ route, permissionLookup }: RouteRowProps) {
    return (
        <div className="flex items-center gap-4 py-2 px-3.5 border-b border-gray-100 last:border-b-0">
            <MethodTag method={route.method} />
            <PathCell path={route.path} />
            <div className="shrink-0 text-right">
                <GuardCell route={route} permissionLookup={permissionLookup} />
            </div>
        </div>
    );
}
